/* ==========================================================================
   LSA Gift With Purchase — cart drawer gift picker.
   --------------------------------------------------------------------------
   Rendered by snippets/lsa-gwp-gift.liquid. The drawer is re-rendered
   server-side by the Section Rendering API on every cart change (see
   assets/cart-drawer.js -> renderContents), so this element is destroyed and
   rebuilt each time and connectedCallback runs again. All STATE lives in
   Liquid; this file only performs the two cart mutations Liquid cannot:
   adding the chosen variant, and taking the gift back when the cart stops
   qualifying.

   The gift is made free by a native automatic "Buy X get Y" discount in the
   admin, not here. Nothing in this file sets or implies a price.
   ========================================================================== */

if (!customElements.get('lsa-gwp-gift')) {
  customElements.define(
    'lsa-gwp-gift',
    class LsaGwpGift extends HTMLElement {
      async connectedCallback() {
        const host = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        /* cart-drawer.js is loaded from theme.liquid, AFTER the scripts in the
           drawer snippet this element renders inside. So <cart-drawer> is still
           an un-upgraded HTMLElement when we first mount, and reading its API
           here throws "getSectionsToRender is not a function". Wait for the
           definition rather than assuming a script order we do not control. */
        if (host) await customElements.whenDefined(host.localName);

        /* A drawer re-render can replace this node while we were waiting. */
        if (!this.isConnected) return;
        this.cart = host;

        const revokeKey = this.dataset.revokeKey;
        if (revokeKey) {
          this.revoke(revokeKey);
          return;
        }

        /* One button per slide, each adding from the select beside it. No
           shared "which one is selected" state to get out of sync, and no
           dependence on which slide happens to be scrolled into view. */
        this.querySelectorAll('[data-gwp-add]').forEach((button) => {
          button.addEventListener('click', this.onAdd.bind(this));
        });

        this.track = this.querySelector('[data-gwp-track]');
        if (this.track) this.initCarousel();
      }

      initCarousel() {
        const prev = this.querySelector('[data-gwp-prev]');
        const next = this.querySelector('[data-gwp-next]');
        if (!prev || !next) return;

        prev.addEventListener('click', () => this.page(-1));
        next.addEventListener('click', () => this.page(1));

        /* Keep the arrows honest about where the track actually is, however it
           got there: arrow, touch swipe, or keyboard scroll. Called directly
           rather than through requestAnimationFrame — it is two reads and two
           attribute writes, and rAF does not run in a hidden document, which
           would leave the arrows stale on a backgrounded tab. */
        this.track.addEventListener('scroll', () => this.syncNav(prev, next), { passive: true });
        this.syncNav(prev, next);
      }

      page(direction) {
        const slide = this.track.querySelector('.lsa-gwp__slide');
        const step = slide ? slide.getBoundingClientRect().width : this.track.clientWidth;
        this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
      }

      syncNav(prev, next) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const atStart = this.track.scrollLeft <= 1;
        const atEnd = this.track.scrollLeft >= max - 1;
        prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }

      get sectionsToRender() {
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          return this.cart.getSectionsToRender().map((section) => section.id);
        }
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      /* The drawer re-renders on success, which destroys this element. Guard
         anyway: a second in-flight request would race its own replacement. */
      lock() {
        if (this.busy) return false;
        this.busy = true;
        return true;
      }

      onAdd(event) {
        if (!this.lock()) return;

        const button = event.currentTarget;
        const slide = button.closest('.lsa-gwp__slide');
        const select = slide && slide.querySelector('.lsa-gwp__select');
        const variantId = select && select.value;
        if (!variantId) {
          this.busy = false;
          return;
        }

        this.setLoading(true, button);

        const body = JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _gwp: 'apollo' } }],
          sections: this.sectionsToRender,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((data) => {
            /* Shopify answers an add failure with 4xx + a description rather
               than throwing, so a sold-out size lands here, not in catch(). */
            if (data.status) {
              this.showError(data.description || data.message);
              return;
            }
            if (this.cart) {
              this.cart.renderContents(data);
            } else {
              window.location = routes.cart_url;
            }
          })
          .catch(() => this.showError(this.dataset.genericError))
          .finally(() => {
            this.busy = false;
            this.setLoading(false, button);
          });
      }

      revoke(lineKey) {
        if (!this.lock()) return;

        const body = JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.sectionsToRender,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
          .then((response) => response.json())
          .then((data) => {
            /* Leave the line alone if the change was rejected. Retrying would
               loop: this element is rebuilt on every render, so a failing
               revoke would fire again the moment the drawer redraws. */
            if (data.status) return;
            if (this.cart) this.cart.renderContents(data);
          })
          .catch(() => {})
          .finally(() => {
            this.busy = false;
          });
      }

      /* Disable every button while one is in flight, so a second column cannot
         be clicked into a request the discount will only pay for once. Only
         the clicked button shows the spinner. */
      setLoading(isLoading, activeButton) {
        this.querySelectorAll('[data-gwp-add]').forEach((button) => {
          button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
          button.classList.toggle('loading', isLoading && button === activeButton);
          const spinner = button.querySelector('.lsa-gwp__spinner');
          if (spinner) spinner.classList.toggle('hidden', !(isLoading && button === activeButton));
        });
      }

      showError(message) {
        const target = this.querySelector('[data-gwp-error]');
        if (!target || !message) return;
        target.textContent = message;
        target.classList.remove('hidden');
      }
    }
  );
}
